import { messagePreviewText } from './messageAttachments.js';

const KEY = 'demandjet.communications';
const DELETED_KEY = 'demandjet.communications.deleted';

function readAll() {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function writeAll(all) {
  localStorage.setItem(KEY, JSON.stringify(all));
}

function readDeletedIds() {
  try {
    const raw = localStorage.getItem(DELETED_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
}

function writeDeletedIds(ids) {
  localStorage.setItem(DELETED_KEY, JSON.stringify(ids));
}

function deletedIdSet() {
  return new Set(readDeletedIds());
}

function rememberDeletedMessage(messageId) {
  const msgId = String(messageId || '');
  if (!msgId) return;
  const existing = readDeletedIds();
  if (existing.includes(msgId)) return;
  writeDeletedIds([...existing, msgId]);
}

function withoutDeleted(messages) {
  const deleted = deletedIdSet();
  if (!deleted.size) return messages || [];
  return (messages || []).filter((message) => !deleted.has(String(message?.ROWID || '')));
}

export function seedDemoMessages(matterId, messages) {
  const all = readAll();
  // Preserve an explicit empty thread after the user deletes every message.
  if (!(matterId in all)) {
    all[matterId] = messages;
    writeAll(all);
  }
  return withoutDeleted(all[matterId] || []);
}

export function getLocalMessages(matterId) {
  const all = readAll();
  return withoutDeleted(all[matterId] || []);
}

export function mergeMessageLists(...lists) {
  const byId = new Map();
  for (const list of lists) {
    for (const message of list || []) {
      const id = String(message?.ROWID || '');
      if (!id) continue;
      if (!byId.has(id)) byId.set(id, message);
    }
  }
  return withoutDeleted(
    Array.from(byId.values()).sort(
      (a, b) => new Date(a.Created_At || 0).getTime() - new Date(b.Created_At || 0).getTime()
    )
  );
}

export function appendLocalMessage(matterId, message) {
  const all = readAll();
  const list = all[matterId] ? [...all[matterId]] : [];
  list.push(message);
  all[matterId] = list;
  writeAll(all);
  return withoutDeleted(list);
}

/** Soft-delete a message locally and keep it from reappearing via merge/seed. */
export function deleteLocalMessage(matterId, messageId) {
  const matterKey = String(matterId || '');
  const msgId = String(messageId || '');
  if (!matterKey || !msgId) return false;

  const all = readAll();
  const existing = Array.isArray(all[matterKey]) ? all[matterKey] : [];
  const next = existing.filter((message) => String(message?.ROWID || '') !== msgId);
  all[matterKey] = next;
  writeAll(all);
  rememberDeletedMessage(msgId);
  return true;
}

/** One conversation per demand record, newest activity first. */
export function listDemandConversations(matters = []) {
  const all = readAll();
  const threads = [];
  const seen = new Set();

  for (const matter of matters || []) {
    const matterId = String(matter.ROWID);
    seen.add(matterId);
    const messages = withoutDeleted(Array.isArray(all[matterId]) ? [...all[matterId]] : []);
    messages.sort(
      (a, b) => new Date(a.Created_At || 0).getTime() - new Date(b.Created_At || 0).getTime()
    );
    const latest = messages[messages.length - 1] || null;
    threads.push({
      matterId,
      matter,
      messages,
      latest,
      preview: messagePreviewText(latest),
      updatedAt: latest?.Created_At || matter.Updated_At || matter.Created_At || null,
      messageCount: messages.length,
    });
  }

  Object.keys(all).forEach((matterId) => {
    if (seen.has(String(matterId))) return;
    const messages = withoutDeleted(Array.isArray(all[matterId]) ? [...all[matterId]] : []);
    if (!messages.length) return;
    messages.sort(
      (a, b) => new Date(a.Created_At || 0).getTime() - new Date(b.Created_At || 0).getTime()
    );
    const latest = messages[messages.length - 1];
    threads.push({
      matterId: String(matterId),
      matter: null,
      messages,
      latest,
      preview: messagePreviewText(latest),
      updatedAt: latest?.Created_At || null,
      messageCount: messages.length,
    });
  });

  return threads.sort(
    (a, b) => new Date(b.updatedAt || 0).getTime() - new Date(a.updatedAt || 0).getTime()
  );
}

/** @deprecated Prefer listDemandConversations — kept for callers that only want active threads. */
export function listLocalMessageThreads(matters = []) {
  return listDemandConversations(matters).filter((thread) => thread.messageCount > 0);
}

export function attachmentsFromMatter(matter) {
  const items = [];
  if (!matter) return items;
  if (matter.Evidence_Vault_URL) {
    const evidenceUrl = String(matter.Evidence_Vault_URL);
    if (evidenceUrl.startsWith('pending-upload://')) {
      const payload = evidenceUrl.slice('pending-upload://'.length);
      const names = payload
        .split(',')
        .map((part) => {
          try {
            return decodeURIComponent(part.trim());
          } catch {
            return part.trim();
          }
        })
        .filter(Boolean);
      if (names.length > 1) {
        names.forEach((name, idx) => {
          items.push({
            id: `evidence_${idx}`,
            name,
            kind: 'Evidence',
            url: `pending-upload://${encodeURIComponent(name)}`,
          });
        });
      } else {
        items.push({
          id: 'evidence',
          name: names[0] || basename(evidenceUrl, 'Evidence package.pdf'),
          kind: 'Evidence',
          url: evidenceUrl,
        });
      }
    } else {
      items.push({
        id: 'evidence',
        name: basename(evidenceUrl, 'Evidence package.pdf'),
        kind: 'Evidence',
        url: evidenceUrl,
      });
    }
  }
  if (matter.Document_Vault_URL) {
    items.push({
      id: 'demand',
      name: basename(matter.Document_Vault_URL, 'Demand letter.pdf'),
      kind: 'Demand letter',
      url: matter.Document_Vault_URL,
    });
  }
  if (Array.isArray(matter.Attachments)) {
    matter.Attachments.forEach((file, idx) => {
      items.push({
        id: file.id || `att_${idx}`,
        name: file.name || `Attachment ${idx + 1}`,
        kind: file.kind || 'Attachment',
        url: file.url || '#',
      });
    });
  }
  return items;
}

function basename(url, fallback) {
  const parts = String(url).split('/');
  return parts[parts.length - 1] || fallback;
}
